import RAPIER from '@dimforge/rapier3d-compat';
import type { Vec } from '../destruction/structure';

export interface FlatSupport {
  owner: number;
  y: number;
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}
interface Corridor {
  version: number;
  stamp?: number;
  owner: number;
  x: number;
  y: number;
  z: number;
  dx: number;
  dz: number;
  length: number;
}
const within = (p: Vec, corridor: Corridor): boolean => {
  const x = p.x - corridor.x,
    z = p.z - corridor.z;
  const along = x * corridor.dx + z * corridor.dz;
  return (
    Math.abs(p.y - corridor.y) < 0.00005 &&
    Math.abs(x * corridor.dz - z * corridor.dx) < 0.00005 &&
    along > -0.00005 &&
    along < corridor.length + 0.00005
  );
};
export interface FootCache {
  corridor?: Corridor;
  rejected?: { version: number; x: number; y: number; z: number };
  obstructions?: { point: Vec; move: Vec; radius: number; version: number; stamp?: number }[];
}
interface Queries {
  version(point: Vec): number;
  revision?(): number;
  support(point: Vec): FlatSupport | undefined;
  staticClear(point: Vec, move: Vec): boolean;
  dynamicSweep(point: Vec, move: Vec): number | undefined;
}

/** Monotonic local revisions. A changed wall/floor invalidates every nearby
 * corridor; distant fractures cannot invalidate clear ground across the city. */
export class FootGeometryChanges {
  private serial = 0;
  private base = 0;
  private tiles = new Map<string, number>();
  get revision(): number {
    return this.serial;
  }
  reset(): void {
    this.base = ++this.serial;
    this.tiles.clear();
  }
  bounds(minX: number, minZ: number, maxX: number, maxZ: number): void {
    const revision = ++this.serial;
    for (let x = Math.floor(minX / 8); x <= Math.floor(maxX / 8); x++)
      for (let z = Math.floor(minZ / 8); z <= Math.floor(maxZ / 8); z++)
        this.tiles.set(`${x},${z}`, revision);
  }
  version(point: Vec, radius = 2.3): number {
    let revision = this.base;
    // Foot corridors default to 2m plus capsule clearance. Other stationary
    // proofs use their actual chassis/look-ahead footprint, including coaches.
    for (let x = Math.floor((point.x - radius) / 8); x <= Math.floor((point.x + radius) / 8); x++)
      for (let z = Math.floor((point.z - radius) / 8); z <= Math.floor((point.z + radius) / 8); z++)
        revision = Math.max(revision, this.tiles.get(`${x},${z}`) ?? this.base);
    return revision;
  }
}

/** Exact straight travel on one unchanged occupied floor cuboid. The entire
 * static corridor is swept before reuse; moving obstacles are swept every step.
 * Corners, stairs, slopes, holes and changed geometry use the ordinary solver. */
export class FootClearance {
  reused = 0;
  built = 0;
  fallback = 0;
  supportReused = 0;
  obstructionReused = 0;
  readonly probe = new RAPIER.Capsule(0.5985, 0.24);
  constructor(private queries: Queries) {}
  private valid(c: Corridor): boolean {
    const stamp = this.queries.revision?.();
    if (stamp !== undefined && stamp === c.stamp) return true;
    if (c.version !== this.queries.version(c)) return false;
    c.stamp = stamp;
    return true;
  }
  reset(): void {
    this.reused = this.built = this.fallback = this.supportReused = this.obstructionReused = 0;
  }
  obstructed(point: Vec, move: Vec, radius: number, cache?: FootCache): boolean {
    const stamp = this.queries.revision?.();
    for (const proof of cache?.obstructions ?? []) {
      if (
        proof.radius !== radius ||
        Math.abs(proof.point.x - point.x) > 1e-8 ||
        Math.abs(proof.point.y - point.y) > 1e-8 ||
        Math.abs(proof.point.z - point.z) > 1e-8 ||
        Math.abs(proof.move.x - move.x) > 1e-8 ||
        Math.abs(proof.move.y - move.y) > 1e-8 ||
        Math.abs(proof.move.z - move.z) > 1e-8
      )
        continue;
      if (stamp === undefined || stamp !== proof.stamp) {
        if (proof.version !== this.queries.version(point)) continue;
        proof.stamp = stamp;
      }
      this.obstructionReused++;
      return true;
    }
    return false;
  }
  /** Only the ordinary solver may record a failed step proven against fixed
   * geometry, including its raised-step alternative. Dynamic blockers never
   * create this proof. A local fracture invalidates it immediately. */
  rememberObstruction(point: Vec, move: Vec, radius: number, cache: FootCache): void {
    const entries = (cache.obstructions ??= []);
    if (entries.length >= 8) entries.shift();
    entries.push({
      point: { ...point },
      move: { ...move },
      radius,
      version: this.queries.version(point),
      stamp: this.queries.revision?.(),
    });
  }
  support(point: Vec, cache?: FootCache): { owner: number; y: number } | undefined {
    const c = cache?.corridor;
    if (!c || !within(point, c) || !this.valid(c)) return;
    this.supportReused++;
    return { owner: c.owner, y: c.y };
  }
  step(
    point: Vec,
    move: Vec,
    reach: number,
    cache: FootCache,
  ): { owner: number; y: number; allowed: number } | undefined {
    let c = cache.corridor;
    let version: number;
    const end = { x: point.x + move.x, y: point.y + move.y, z: point.z + move.z };
    if (c && within(point, c) && within(end, c) && this.valid(c)) this.reused++;
    else {
      cache.corridor = c = undefined;
      version = this.queries.version(point);
      const rejected = cache.rejected;
      if (
        rejected?.version === version &&
        Math.hypot(point.x - rejected.x, point.y - rejected.y, point.z - rejected.z) < 0.5
      ) {
        this.fallback++;
        return;
      }
      const patch = this.queries.support(point);
      const distance = Math.hypot(move.x, move.z);
      if (patch && distance > 1e-6 && Math.abs(patch.y - point.y) < 0.00005) {
        const dx = move.x / distance,
          dz = move.z / distance;
        const margin = 0.025;
        let length = Math.min(2, reach);
        if (
          point.x < patch.minX + margin ||
          point.x > patch.maxX - margin ||
          point.z < patch.minZ + margin ||
          point.z > patch.maxZ - margin
        )
          length = 0;
        if (Math.abs(dx) > 1e-8)
          length = Math.min(length, ((dx > 0 ? patch.maxX - margin : patch.minX + margin) - point.x) / dx);
        if (Math.abs(dz) > 1e-8)
          length = Math.min(length, ((dz > 0 ? patch.maxZ - margin : patch.minZ + margin) - point.z) / dz);
        if (
          length > Math.max(0.15, distance) &&
          this.queries.staticClear(point, { x: dx * length, y: 0, z: dz * length })
        ) {
          c = {
            version,
            stamp: this.queries.revision?.(),
            owner: patch.owner,
            x: point.x,
            y: patch.y,
            z: point.z,
            dx,
            dz,
            length,
          };
          cache.corridor = c;
          cache.rejected = undefined;
          this.built++;
        }
      }
      if (!c) {
        cache.rejected = { version, x: point.x, y: point.y, z: point.z };
        this.fallback++;
        return;
      }
    }
    const allowed = this.queries.dynamicSweep(point, move);
    if (allowed === undefined) {
      this.fallback++;
      return;
    }
    return { owner: c.owner, y: c.y, allowed };
  }
}
